// Render-time guard for annotation bodies. The save path sanitises a note against a tag allow-list,
// but that is not the only writer: the Skipper report ingest stores the body it is given verbatim,
// so a note can reach the page carrying markup the save path would have stripped. The parser turns
// whatever it is handed into live elements, so the allow-list is re-applied here at render.
//
// A disallowed tag is unwrapped rather than dropped — its text survives, the element does not.
// Children are re-parsed with the caller's own options so the guard recurses into them; without
// that, markup nested inside an unwrapped tag would reach the DOM unchecked.
import React from 'react'
import { domToReact } from 'html-react-parser'

// The tag set the save path allows, plus the link elements the annotation renderer resolves itself
// and the anchor the linkifier produces for a bare URL.
export const NOTE_ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'cf-link',
  'cq-link',
  'cvq-link',
  'h1',
  'i',
  'open-sidebar-link',
  'q-link',
  'u',
  'verbatim-link',
  'vq-link'
])

// Unwrapping these would surface their source as prose, so they are dropped whole.
const DROPPED_TAGS = new Set(['script', 'style'])

// Only these schemes may survive on a link; everything else (`javascript:`, `data:`, …) loses the
// anchor and keeps the text.
const SAFE_LINK_SCHEME = /^(?:https?:|mailto:|[#/])/i

interface NoteNode {
  name?: string
  attribs?: Record<string, string>
  children?: unknown[]
}

const isEventHandler = (attribute: string): boolean => attribute.toLowerCase().startsWith('on')

const hasEventHandler = (node: NoteNode): boolean => Object.keys(node.attribs ?? {}).some(isEventHandler)

const hasUnsafeHref = (node: NoteNode): boolean =>
  'a' === node.name && !SAFE_LINK_SCHEME.test((node.attribs?.href ?? '').trim())

const withoutEventHandlers = (attribs: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(attribs).filter(([name]) => !isEventHandler(name)))

// Returns the replacement for a node that must not render as it stands, or `undefined` when the
// node is safe and the parser's own handling applies. `options` is the caller's parser options, so
// the guard applies to every level of the tree.
export const sanitizeNoteNode = (node: NoteNode, options: object = {}): React.ReactElement | undefined => {
  if (!node.name) return void 0 // text, comment, or directive — nothing to mount

  const children = (): React.ReactNode => domToReact((node.children ?? []) as never, options as never)

  if (DROPPED_TAGS.has(node.name)) return React.createElement(React.Fragment)
  if (!NOTE_ALLOWED_TAGS.has(node.name) || hasUnsafeHref(node)) {
    return React.createElement(React.Fragment, null, children())
  }
  if (hasEventHandler(node)) {
    return React.createElement(node.name, withoutEventHandlers(node.attribs ?? {}), children())
  }
  return void 0
}
